import { Observable, throwError as observableThrowError } from 'rxjs';
import {
  HttpClient,
  HttpHeaders,
  HttpParams,
  HttpResponse,
  HttpUrlEncodingCodec,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { extend, forOwn, identity, isEmpty, pickBy } from 'lodash';
import { v4 } from 'uuid';
import {
  CONTENT_TYPE_HEADER,
  IHttpService,
  IRequest,
  IResponseInfo,
  LONG_REQUEST_TIMEOUT,
  REQUEST_TIMEOUT,
  RequestMethod,
  ResponseContentType,
} from './http.data';
import {
  catchError,
  finalize,
  map,
  share,
  timeout as observableTimeout,
} from 'rxjs/operators';

export class HttpQueryEncoder extends HttpUrlEncodingCodec {
  encodeValue(v: string): string {
    return encodeURIComponent(v);
  }
}

@Injectable()
export class HttpService implements IHttpService {
  private _baseHeaders: any = {
    [CONTENT_TYPE_HEADER]: 'application/json; charset=utf-8',
  };
  private _outboundRequests: {
    [id: string]: Observable<HttpResponse<any>>;
  } = {};

  constructor(private _httpClient: HttpClient) {}

  request(
    method: RequestMethod,
    url: string,
    body: any,
    headers: any = {},
    storeRequest?: boolean,
    responseContentType?: ResponseContentType
  ): Observable<IResponseInfo> {
    switch (method) {
      case 'GET':
        return this.get(url, headers, body, responseContentType);
      case 'POST':
        return this.post(url, body, headers, storeRequest, responseContentType);
      case 'PUT':
        return this.put(url, body, headers, storeRequest, responseContentType);
      case 'DELETE':
        return this.delete(url, body, headers, storeRequest);
      default:
        return observableThrowError({
          method: method,
          message: 'method not supported',
        });
    }
  }

  get(
    url: string,
    headers: any = {},
    params: any = null,
    responseContentType: ResponseContentType = 'json',
    externalUrl: boolean = false
  ): Observable<IResponseInfo> {
    return this.handleRequest({
      method: 'GET',
      url,
      params,
      headers,
      responseType: responseContentType,
      timeout:
        responseContentType === 'json' ? REQUEST_TIMEOUT : LONG_REQUEST_TIMEOUT,
    });
  }

  post(
    url: string,
    body: any = {},
    headers: any = {},
    storeRequest: boolean = true,
    responseContentType: ResponseContentType = 'json'
  ): Observable<IResponseInfo> {
    return this.handleRequest({
      method: 'POST',
      url,
      body,
      headers,
      storeRequest,
      responseType: responseContentType,
    });
  }

  put(
    url: string,
    body: any = {},
    headers: any = {},
    storeRequest: boolean = true,
    responseContentType: ResponseContentType = 'json'
  ): Observable<IResponseInfo> {
    return this.handleRequest({
      method: 'PUT',
      url,
      body,
      headers,
      storeRequest,
      responseType: responseContentType,
    });
  }

  delete(
    url: string,
    body: any = {},
    headers: any = {},
    storeRequest: boolean = true
  ): Observable<IResponseInfo> {
    return this.handleRequest({
      method: 'DELETE',
      url,
      body,
      headers,
      storeRequest,
      responseType: 'json',
    });
  }

  anyOutboundRequests() {
    return !isEmpty(this._outboundRequests);
  }

  handleRequest({
    method,
    url,
    body,
    params,
    headers,
    storeRequest = false,
    responseType = 'json',
    timeout = REQUEST_TIMEOUT,
  }: IRequest): Observable<any> {
    const requestId = v4();
    const requestHeaders: HttpHeaders = this.getRequestHeaders(headers);
    const req = this._httpClient.request(method, url, {
      headers: requestHeaders,
      params: params ? new HttpParams({ fromObject: params }) : null,
      body: this.getPayload(body, requestHeaders),
      observe: 'response',
      responseType,
    });

    if (storeRequest) {
      this._outboundRequests[requestId] = req;
    }

    return req.pipe(
      observableTimeout(timeout),
      map(
        (response: HttpResponse<any>): IResponseInfo => {
          const responseHeaders: HttpHeaders = response.headers;

          switch (responseType) {
            case 'json':
            case 'blob':
            case 'text':
              return {
                headers: responseHeaders,
                body: response.body,
                status: response.status,
              };
            case 'arraybuffer':
              return {
                headers: responseHeaders,
                body: [responseHeaders.get(CONTENT_TYPE_HEADER), response.body],
                status: response.status,
              };
            default:
              return {
                headers: responseHeaders,
                body: {},
                status: response.status,
              };
          }
        }
      ),
      catchError((error) => {
        // TODO: create an error store that stores the auth and error info
        // so that we don't run into circular dependencies
        // this._errorService.alertSystem(
        //   error,
        //   {
        //     method,
        //     url,
        //     body,
        //     headers,
        //     storeRequest,
        //     responseType,
        //     timeout
        //   }
        // );

        return observableThrowError([error.status, error]);
      }),
      finalize(() => delete this._outboundRequests[requestId]),
      share()
    );
  }

  getRequestHeaders(headers: any = {}): HttpHeaders {
    return new HttpHeaders(
      pickBy(extend({}, this._baseHeaders, headers), identity)
    );
  }

  getPayload(body: any, headers?: HttpHeaders): any {
    if (
      body &&
      headers &&
      headers.get(CONTENT_TYPE_HEADER) === 'application/x-www-form-urlencoded'
    ) {
      let payload = new HttpParams({
        fromString: '',
        encoder: new HttpQueryEncoder(),
      });

      forOwn(
        body,
        (value: string, key: string) => (payload = payload.append(key, value))
      );

      return payload;
    }

    return body;
  }
}
